import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { map, shareReplay, startWith, tap } from 'rxjs/operators';
import { DeviceTypeName } from '@bo-client/activation-management/shared/commissioning-constans';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-gr-meter-formpart',
  templateUrl: './gr-meter-formpart.component.html',
  styleUrls: ['./gr-meter-formpart.component.scss']
})
export class GrMeterFormpartComponent implements OnInit {
  #destroyRef = inject(DestroyRef);
  constructor(private dataShareService: DataShareService) {
    // Empty
  }

  hasMeterTwo = false;
  hasMeterThree = false;
  isMeterValuesRequired = false;
  noMeterPresent = false;

  @Input() formGroup: FormGroup;
  @Input() hasDefiniteMeter = false;
  @Input() disable = false;

  meterNumberControl: FormControl;
  deviceTypeControl: FormControl;

  meterOneValueControl: FormControl;
  meterTwoValueControl: FormControl;
  meterThreeValueControl: FormControl;

  meterOneObisControl: FormControl;
  meterTwoObisControl: FormControl;
  meterThreeObisControl: FormControl;

  ngOnInit() {
    this.meterNumberControl = this.formGroup.get('meterNumber') as FormControl;
    this.deviceTypeControl = this.formGroup.get('deviceType') as FormControl;

    this.meterOneValueControl = this.formGroup.get(['meterOne', 'value']) as FormControl;
    this.meterTwoValueControl = this.formGroup.get(['meterTwo', 'value']) as FormControl;
    this.meterThreeValueControl = this.formGroup.get(['meterThree', 'value']) as FormControl;

    this.meterOneObisControl = this.formGroup.get(['meterOne', 'obisCode']) as FormControl;
    this.meterTwoObisControl = this.formGroup.get(['meterTwo', 'obisCode']) as FormControl;
    this.meterThreeObisControl = this.formGroup.get(['meterThree', 'obisCode']) as FormControl;
    this.#fixTransientDeviceType();

    this.dataShareService.tenantConfig$
      .pipe(
        map((config) => config.gridRegistration?.requireMeterReadingForCommissioningProtocol || false),
        tap((value) => {
          this.isMeterValuesRequired = value;
          this.setValidators();
        })
      )
      .subscribe();
    this.deviceTypeControl?.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef), startWith(this.deviceTypeControl?.value), shareReplay(1))
      .subscribe((deviceType: DeviceTypeName) => {
        if (!deviceType) {
          return;
        }
        this.#onDeviceTypeChange(deviceType);
      });
  }

  setValidators(): void {
    const setControlValidators = (
      control: AbstractControl,
      validators: ((control: AbstractControl) => ValidationErrors)[] | null,
      clearValue = false
    ) => {
      if (validators) {
        control.setValidators(validators);
      } else {
        control.clearValidators();
        if (clearValue) {
          control.setValue('');
        }
      }
      control.updateValueAndValidity();
    };

    if (
      this.isMeterValuesRequired &&
      ((this.maybeHasNoMeter && !this.noMeterPresent) || this.hasAlreadyAMeter)
    ) {
      setControlValidators(this.meterNumberControl, [Validators.required]);
      setControlValidators(this.meterNumberControl, [Validators.required]);
      setControlValidators(this.meterOneValueControl, [Validators.required]);
      setControlValidators(
        this.meterTwoValueControl,
        this.hasMeterTwo ? [Validators.required] : null,
        !this.hasMeterTwo
      );
      setControlValidators(
        this.meterThreeValueControl,
        this.hasMeterThree ? [Validators.required] : null,
        !this.hasMeterThree
      );
    } else {
      setControlValidators(this.meterNumberControl, null);
      setControlValidators(this.meterOneValueControl, null);
      setControlValidators(this.meterTwoValueControl, null, !this.hasMeterTwo);
      setControlValidators(this.meterThreeValueControl, null, !this.hasMeterThree);
    }
  }

  #onDeviceTypeChange(deviceType: DeviceTypeName): void {
    this.hasMeterTwo = [
      DeviceTypeName.Zweitarif,
      DeviceTypeName.Zweirichtung,
      DeviceTypeName.ZweirichtungZweitarif
    ].some((type) => type === deviceType);

    this.hasMeterThree = deviceType === DeviceTypeName.ZweirichtungZweitarif;

    this.setObisCodes(deviceType);
    this.setValidators();
  }

  onNoMeterPresentChange(checked: boolean) {
    this.noMeterPresent = checked;
    this.setValidators();
  }

  private setObisCodes(deviceType: DeviceTypeName): void {
    const config = this.dataShareService.tenantConfig.commissioning?.powerConfig || {};

    /**
     * Speichert die Obis-Codes für die Zähler basierend auf dem DeviceType.
     * @property {string} meterOne - Obis-Code für den ersten Zähler.
     * @property {string} meterTwo - Obis-Code für den zweiten Zähler. (Leer, wenn nicht vorhanden)
     * @property {string} meterThree - Obis-Code für den dritten Zähler. (Leer, wenn nicht vorhanden)
     */
    const obisCodes = {
      [DeviceTypeName.Eintarif]: {
        meterOne: config.defaultM1ObisCodeSingleTariffMeter || '1.8.0',
        meterTwo: '',
        meterThree: ''
      },
      [DeviceTypeName.Zweitarif]: {
        meterOne: config.defaultM1ObisCode || '1.8.1',
        meterTwo: config.defaultM2ObisCode || '1.8.2',
        meterThree: ''
      },
      [DeviceTypeName.Zweirichtung]: {
        meterOne: config.defaultM1ObisCodeBiDirectionalMeter || '1.8.0',
        meterTwo: config.defaultM2ObisCodeBiDirectionalMeter || '2.8.0',
        meterThree: ''
      },
      [DeviceTypeName.ZweirichtungZweitarif]: {
        meterOne: config.defaultM1ObisCode || '1.8.1',
        meterTwo: config.defaultM2ObisCode || '1.8.2',
        meterThree: config.defaultM2ObisCodeBiDirectionalMeter || '2.8.0'
      },
      [DeviceTypeName.Erzeuger]: {
        meterOne: config.defaultM1ObisCodeProducerMeter || '2.8.0',
        meterTwo: '',
        meterThree: ''
      }
    };

    this.meterOneObisControl.patchValue(obisCodes[deviceType].meterOne);
    this.meterTwoObisControl.patchValue(obisCodes[deviceType].meterTwo);
    this.meterThreeObisControl.patchValue(obisCodes[deviceType].meterThree);

    this.meterOneObisControl.updateValueAndValidity();
    this.meterTwoObisControl.updateValueAndValidity();
    this.meterThreeObisControl.updateValueAndValidity();
  }

  get hasErrors(): boolean {
    const meterOneControl = this.formGroup.controls.meterOne;
    const meterTwoControl = this.formGroup.controls.meterTwo;
    const meterNumberControl = this.formGroup.controls.meterNumber;

    return (
      (!!meterOneControl && meterOneControl.invalid && meterOneControl.touched) ||
      (!!meterTwoControl && meterTwoControl.invalid && meterTwoControl.touched) ||
      (!!meterNumberControl && meterNumberControl.invalid && meterNumberControl.touched)
    );
  }

  get hasAlreadyAMeter(): boolean {
    return this.hasDefiniteMeter;
  }

  get maybeHasNoMeter(): boolean {
    return !this.hasDefiniteMeter;
  }

  protected readonly deviceTypeNameOptions = Object.values(DeviceTypeName);

  /**
   * Diese Methode ist notwendig, da der DeviceType erst nachträglich persistent gespeichert wurde und in alten Netzanmeldungen noch nicht gesetzt ist.
   * Der deviceType wird anhand der vorhandenen Zählwerke und deren ObisCodes gesetzt.
   */
  #fixTransientDeviceType(): void {
    if (!this.deviceTypeControl || this.deviceTypeControl.value) {
      return; // Wert ist bereits gesetzt, oder das Control existiert nicht
    }

    const meterThree = this.formGroup.get('meterThree')?.value?.value;
    const meterTwo = this.formGroup.get('meterTwo')?.value?.value;
    const meterOne = this.formGroup.get('meterOne')?.value?.value;

    // Wir hoffen darauf, dass die Kunden nicht von den Standardwerten der ObisCodes abweichen (zumindest bei der ersten Ziffer)
    if (meterThree) {
      this.deviceTypeControl.setValue(DeviceTypeName.ZweirichtungZweitarif);
    } else if (meterTwo) {
      const isBiDirectional = this.formGroup.get('meterTwo')?.value?.obisCode.startsWith('2');
      this.deviceTypeControl.setValue(
        isBiDirectional ? DeviceTypeName.Zweirichtung : DeviceTypeName.Zweitarif
      );
    } else if (meterOne) {
      const isProducer = this.formGroup.get('meterOne')?.value?.obisCode.startsWith('2');
      this.deviceTypeControl.setValue(isProducer ? DeviceTypeName.Erzeuger : DeviceTypeName.Eintarif);
    } else {
      this.deviceTypeControl.setValue(DeviceTypeName.Eintarif);
    }
  }
}
